/**
 * 진로·전공·직업 추천 엔진
 *
 * 원리:
 *  1) 흥미(Holland RIASEC)가 진로의 주축이 되도록, 흥미를 3 : 가치관 1 비율로 반영한다.
 *  2) 각 흥미유형/가치관은 실제 점수 크기(0~1 정규화)에 비례해 계열에 기여한다.
 *  3) 계열 내 전공은 흥미유형별 적합도 순으로 정렬해, 가장 잘 맞는 전공이 먼저 나온다.
 *
 * 예) 수학·과학·탐구를 좋아하는 학생(탐구형 I) → 자연계열/공학계열이 1순위로,
 *     수학과·물리학과·생명과학과 등이 상위에 오고 인문계열(국어국문학과)은 나오지 않는다.
 */

import { careerTracks, type Major } from '@/mocks/careerData';

/* ─────────── 흥미유형 → 계열 선호도 (0~1) ─────────── */
// 흥미가 진로의 주축이므로, 유형마다 가장 잘 맞는 계열에 최고 가중치를 둔다.
const HOLLAND_TRACK_AFFINITY: Record<string, Record<string, number>> = {
  R: { engineering: 1.0, science: 0.6 },
  I: { science: 1.0, engineering: 0.85 },
  A: { arts: 1.0, humanities: 0.55 },
  S: { education: 1.0, social: 0.85 },
  E: { social: 1.0, humanities: 0.4 },
  C: { social: 0.7, engineering: 0.5, science: 0.5 },
};

/* ─────────── 가치관 → 계열 선호도 (0~1, 2차 신호) ─────────── */
const VALUE_TRACK_AFFINITY: Record<string, Record<string, number>> = {
  achievement: { engineering: 1.0, science: 0.8 },
  helping: { education: 1.0, social: 0.9 },
  creativity: { arts: 1.0, humanities: 0.4 },
  intellectual: { science: 1.0, engineering: 0.6 },
  leadership: { social: 1.0, engineering: 0.6 },
  stability: { social: 0.8, education: 0.8, science: 0.4 },
};

/* ─────────── 흥미유형별 전공 적합도 우선순위 (앞일수록 잘 맞음) ─────────── */
const HOLLAND_MAJOR_PRIORITY: Record<string, string[]> = {
  R: [
    '기계공학과', '전자공학과', '컴퓨터공학과', '철도운수학과', '항공운항학과',
    '환경공학과', '물리학과', '체육교육과', '물리치료학과', '소방행정학과',
  ],
  I: [
    '수학과', '물리학과', '생명과학과', '의예과', '약학과', '컴퓨터공학과',
    '인공지능학과', '전자공학과', '기계공학과', '환경공학과', '심리학과', '경제학과',
  ],
  A: [
    '시각디자인학과', '미술교육과', '문화콘텐츠학과', '국어국문학과', '철학과',
    '조리·제과제빵학과', '체육교육과',
  ],
  S: [
    '교육학과', '초등교육과', '심리학과', '정치외교학과', '물리치료학과', '소방행정학과',
  ],
  E: ['경영학과', '경제학과', '정치외교학과', '소방행정학과'],
  C: ['경제학과', '경영학과', '수학과', '컴퓨터공학과', '환경공학과', '소방행정학과'],
};

// 흥미 : 가치관 반영 비율
const HOLLAND_WEIGHT = 3;
const VALUE_WEIGHT = 1;

export interface CareerRecommendation {
  trackIds: string[]; // 추천 계열 (상위 3개)
  majors: Major[]; // 흥미 적합도 순으로 정렬된 전공
  jobs: string[]; // 전공에서 파생된 추천 직업 (중복 제거, 적합도 순)
  trackScores: Record<string, number>; // 계열별 적합 점수 (디버깅·표시용)
}

function normalize(scores: Record<string, number>, max: number): Record<string, number> {
  const result: Record<string, number> = {};
  for (const [key, val] of Object.entries(scores)) {
    result[key] = max > 0 ? Math.max(0, Math.min(1, val / max)) : 0;
  }
  return result;
}

/** 상위 흥미유형을 점수 순으로 정렬해 반환 */
function rankHolland(hollandNorm: Record<string, number>): string[] {
  return Object.entries(hollandNorm)
    .sort((a, b) => b[1] - a[1])
    .map(([code]) => code);
}

/** 전공 이름 → 흥미유형 우선순위 순번 (작을수록 적합) */
function majorPriority(name: string, topHolland: string[]): number {
  const list0 = HOLLAND_MAJOR_PRIORITY[topHolland[0]] || [];
  const pos0 = list0.indexOf(name);
  if (pos0 >= 0) return pos0;

  const list1 = HOLLAND_MAJOR_PRIORITY[topHolland[1]] || [];
  const pos1 = list1.indexOf(name);
  if (pos1 >= 0) return pos1 + 1000;

  return 2000; // 매칭 없음 → 계열 내 데이터 순서 유지
}

export function recommendCareer(
  hollandScores: Record<string, number>,
  valueScores: Record<string, number>,
  hollandMax: number,
  valueMax: number,
): CareerRecommendation {
  const hollandNorm = normalize(hollandScores, hollandMax);
  const valueNorm = normalize(valueScores, valueMax);

  // 1) 계열 점수 산출: 흥미(×3) + 가치관(×1), 점수 크기에 비례
  const trackScores: Record<string, number> = {};

  Object.entries(hollandNorm).forEach(([code, norm]) => {
    const aff = HOLLAND_TRACK_AFFINITY[code] || {};
    Object.entries(aff).forEach(([track, w]) => {
      trackScores[track] = (trackScores[track] || 0) + norm * w * HOLLAND_WEIGHT;
    });
  });

  Object.entries(valueNorm).forEach(([val, norm]) => {
    const aff = VALUE_TRACK_AFFINITY[val] || {};
    Object.entries(aff).forEach(([track, w]) => {
      trackScores[track] = (trackScores[track] || 0) + norm * w * VALUE_WEIGHT;
    });
  });

  // 2) 상위 3개 계열
  const rankedTracks = Object.entries(trackScores).sort((a, b) => b[1] - a[1]);
  const trackIds = rankedTracks.slice(0, 3).map(([id]) => id);

  // 3) 흥미유형 우선순위
  const topHolland = rankHolland(hollandNorm);

  // 4) 전공 수집 + 적합도 순 정렬
  interface ScoredMajor {
    major: Major;
    trackScore: number;
    priority: number;
  }
  const scored: ScoredMajor[] = [];

  trackIds.forEach((id) => {
    const track = careerTracks.find((t) => t.id === id);
    if (!track) return;
    const score = trackScores[id] || 0;
    track.fields.forEach((f) =>
      f.majors.forEach((m) => {
        scored.push({ major: m, trackScore: score, priority: majorPriority(m.name, topHolland) });
      }),
    );
  });

  scored.sort((a, b) => {
    if (Math.abs(b.trackScore - a.trackScore) > 0.0001) return b.trackScore - a.trackScore;
    return a.priority - b.priority;
  });

  const majors = scored.map((s) => s.major);

  // 5) 직업: 전공 적합도 순으로 중복 제거
  const jobs = Array.from(new Set(majors.flatMap((m) => m.relatedJobs)));

  return { trackIds, majors, jobs, trackScores };
}